import { memo, useContext, useState, type CSSProperties } from 'react'
import { ListReady } from './listReady'
import type { Item } from '../../lib/supabase'
import type { RowVisual } from '../../hooks/useSwipeRows'
import { BasketIcon, PencilIcon, TrashIcon } from '../icons'

// One list row with its two swipe tracks (ux/03 ItemRow): the lime basket
// track revealed from the left when the row moves right, the destructive
// track with a real Delete button revealed from the right when it moves left.
// The row itself is opaque and covers both; it only ever translates (no tilt).
//
// Shop (shopping mode): [check 44×44] [name + meta]; tapping the row edits.
// List (the phone's list, for planning): [category dot] [name + meta]; tap edits, swipe left deletes.
// Plan (desktop): [lead-in] [name + meta] [edit] [delete], no check, no basket.

export interface SwipeRowProps {
  item: Item
  tint: string
  mode: 'shop' | 'list' | 'plan'
  visual: RowVisual
  bind: Record<string, unknown>
  /** Shopping mode's ring: into the basket. */
  onCheck?: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}

export const SwipeRow = memo(function SwipeRow({
  item, tint, mode, visual, bind, onCheck, onEdit, onDelete,
}: SwipeRowProps) {
  // A row that mounts after the list's first paint is new (added, restored,
  // put back) and settles in; the list a screen opens with does not animate.
  const ready = useContext(ListReady)
  const [isNew] = useState(ready)
  const { dx, dragging, armed, label, deleteWidth, deleteArmed, deleteLabel, deleteButtonFocusable, leaving } = visual
  const meta = [item.quantity, item.note].filter(Boolean).join(' · ')
  const state = leaving ? `is-leaving-${leaving}` : dragging ? 'is-dragging' : ''
  // Always the finger's position; the leaving classes override it with the
  // slide-out, except under reduced motion, where the row fades where it is.
  const rowStyle: CSSProperties = { transform: `translateX(${dx}px)` }
  const deleteTrack = leaving === 'left' ? '100%' : `${deleteWidth}px`

  return (
    <li className={`ml-swipe ${state}${isNew ? ' is-new' : ''}`} style={{ '--ml-tint': tint } as CSSProperties}>
      {mode === 'shop' && (
        <div className={`ml-track ml-track--basket${armed ? ' is-armed' : ''}`} aria-hidden="true"
          style={{ width: leaving === 'right' ? '100%' : `${Math.max(0, dx)}px` }}>
          <BasketIcon size={18} />
          <span className="ml-label">{label}</span>
        </div>
      )}
      <div className={`ml-track ml-track--delete${deleteArmed ? ' is-armed' : ''}`} style={{ width: deleteTrack }}>
        <button
          type="button"
          data-swipe-ignore
          tabIndex={deleteButtonFocusable ? 0 : -1}
          aria-hidden={deleteButtonFocusable ? undefined : true}
          aria-label={`Delete ${item.name}`}
          onClick={() => onDelete(item.id)}
        >
          <TrashIcon size={18} />
          <span className="ml-label">{deleteLabel}</span>
        </button>
      </div>

      <div className={`ml-row ml-row--${mode}`} style={rowStyle} {...bind}>
        {mode === 'shop'
          ? (
            <button type="button" className="ml-check" data-swipe-ignore
              aria-label={`Put ${item.name} in the basket`} onClick={() => onCheck?.(item.id)}>
              <span className="ml-check__ring" aria-hidden="true" />
            </button>
          )
          : mode === 'list'
            ? <span className="ml-row__dot" aria-hidden="true"><span className="ml-dot" /></span>
            : <span className="ml-row__lead" aria-hidden="true" />}

        {/* A pointer tap is the gesture reducer's to call (a drag is not a tap);
            this click only answers the keyboard (Enter/Space give detail 0). */}
        <button type="button" className="ml-row__main" aria-label={mode === 'plan' ? undefined : `Edit ${item.name}`}
          onClick={e => { if (e.detail === 0) onEdit(item.id) }}>
          <span className="ml-row__name">{item.name}</span>
          {meta && <span className="ml-row__meta">{meta}</span>}
        </button>

        {mode === 'plan' && (
          <span className="ml-row__actions">
            <button type="button" className="ml-row__action" data-swipe-ignore aria-label={`Edit ${item.name}`}
              onClick={() => onEdit(item.id)}>
              <PencilIcon size={18} />
            </button>
            <button type="button" className="ml-row__action" data-swipe-ignore aria-label={`Delete ${item.name}`}
              onClick={() => onDelete(item.id)}>
              <TrashIcon size={18} />
            </button>
          </span>
        )}
      </div>
    </li>
  )
})
